/**
 * UserMenu Component
 *
 * Dropdown menu triggered by the user avatar in the AppBar.
 * Contains: username display → Preferences section (with ThemeToggle) → Logout.
 *
 * The menu stays open while the user interacts with the Preferences section;
 * it only closes when the user clicks outside or explicitly selects Logout.
 *
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5, 10.6
 */

import React, { useState } from 'react';
import {
    Avatar,
    Badge,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Menu,
    MenuItem,
    Switch,
    Typography,
} from '@mui/material';
import { CreditCard, ExpandLess, ExpandMore, Inbox as InboxIcon, Notifications as NotificationsIcon, Person, Receipt, Tune, Logout } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from './ThemeToggle';
import { useUserProfile } from '../hooks/useUserProfile';
import { useNotificationPreferences } from '../hooks/useNotificationPreferences';
import { resolveDisplayName } from '../utils/user-display';
import { formatCreditsBalance } from '../utils/credit-utils';

const tierColors: Record<string, string> = {
    Free: '#4caf50',
    Iron: '#546e7a',
    Bronze: '#8d6e63',
    Silver: '#b0bec5',
    Gold: '#ffc107',
};

const TIER_ORDER = ['Free', 'Iron', 'Bronze', 'Silver', 'Gold'];
function tierProgress(tier?: string): number {
    const idx = TIER_ORDER.indexOf(tier ?? 'Free');
    if (idx < 0) return 0;
    return (idx / (TIER_ORDER.length - 1)) * 100;
}

/** Compact email notification toggle for the dropdown menu */
function NotificationPreferencesCompact() {
    const { preferences, isLoading, updatePreference, isMutating } = useNotificationPreferences();

    if (isLoading || !preferences) {
        return <Typography variant="caption" color="text.secondary">Loading…</Typography>;
    }

    const emailEnabled = preferences.email_enabled ?? false;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">
                Email {emailEnabled ? '(On)' : '(Off)'}
            </Typography>
            <Switch
                size="small"
                checked={emailEnabled}
                onChange={() => updatePreference({ email_enabled: !emailEnabled })}
                disabled={isMutating}
                inputProps={{ 'aria-label': 'Toggle email notifications' }}
            />
        </Box>
    );
}

export interface UserMenuProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    unreadCount?: number;
    onInboxClick?: () => void;
    onSettingsClick?: () => void;
}

export function UserMenu({ anchorEl, open, onClose, unreadCount = 0, onInboxClick, onSettingsClick: _onSettingsClick }: UserMenuProps) {
    const { user, email } = useAuth();
    const navigate = useNavigate();
    const [prefsOpen, setPrefsOpen] = useState(false);
    const [notifsOpen, setNotifsOpen] = useState(false);
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const { profile, isLoading, error } = useUserProfile();

    const displayName = resolveDisplayName({
        username: profile?.username,
        display_name: profile?.display_name || user?.username,
        email,
    });

    const avatarUrl = profile?.avatar_url;

    const handleLogout = async () => {
        setLogoutConfirmOpen(false);
        onClose();
        // Sign out by calling Amplify directly and immediately reloading.
        // We bypass AuthContext's signOut() to avoid the React state update
        // (setUser(null)) that would trigger ProtectedRoute → /login redirect
        // before the page reload fires.
        try {
            const { signOut: amplifySignOut } = await import('aws-amplify/auth');
            await amplifySignOut();
        } catch (error) {
            console.error('Logout failed:', error);
        }
        window.location.href = '/';
    };

    const handleTogglePrefs = (e: React.MouseEvent) => {
        // Prevent the Menu from closing when clicking Preferences
        e.stopPropagation();
        setPrefsOpen((prev) => !prev);
    };

    const handleToggleNotifs = (e: React.MouseEvent) => {
        e.stopPropagation();
        setNotifsOpen((prev) => !prev);
    };

    const handleInboxClick = () => {
        onInboxClick?.();
        onClose();
    };

    return (
        <>
            <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={onClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: { sx: { minWidth: 230 } },
                }}
            >
                {/* User identity: avatar + username */}
                <MenuItem disabled sx={{ opacity: '1 !important' }} data-testid="user-menu-username">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {avatarUrl ? (
                            <Avatar
                                src={avatarUrl}
                                alt={displayName}
                                sx={{ width: 36, height: 36 }}
                                data-testid="user-menu-avatar"
                            />
                        ) : (
                            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 16 }} data-testid="user-menu-avatar-fallback">
                                {(displayName || 'U').charAt(0).toUpperCase()}
                            </Avatar>
                        )}
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                            {displayName}
                        </Typography>
                    </Box>
                </MenuItem>

                {/* Account badges — compact for dropdown */}
                <Box sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }} data-testid="user-menu-account-badges">
                    {error ? (
                        <Typography variant="caption" color="error" data-testid="user-menu-profile-error">
                            Unable to load profile
                        </Typography>
                    ) : isLoading ? (
                        <Typography variant="caption" color="text.secondary">Loading…</Typography>
                    ) : (
                        <>
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Credits</Typography>
                                    <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                        {formatCreditsBalance(profile?.credits_remaining ?? 0)} available
                                    </Typography>
                                </Box>
                                <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                                    <Box sx={{
                                        height: '100%',
                                        width: `${(profile?.total_credits ?? 0) > 0 ? ((profile?.credits_remaining ?? 0) / (profile?.total_credits ?? 1)) * 100 : 0}%`,
                                        borderRadius: 3,
                                        bgcolor: '#4caf50',
                                    }} />
                                </Box>
                            </Box>
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Tier</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: tierColors[profile?.tier ?? 'Free'] || '#1976d2' }}>
                                        {profile?.tier ?? 'Free'}
                                    </Typography>
                                </Box>
                                <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                                    <Box sx={{
                                        height: '100%',
                                        width: `${tierProgress(profile?.tier)}%`,
                                        borderRadius: 3,
                                        bgcolor: tierColors[profile?.tier ?? 'Free'] || '#1976d2',
                                    }} />
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>

                {/* — Account section — */}
                <Typography variant="overline" sx={{ px: 2, pt: 1.5, pb: 0.25, display: 'block', color: 'text.disabled', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                    Account
                </Typography>

                {/* Profile */}
                <MenuItem onClick={() => { navigate('/account/profile'); onClose(); }} data-testid="user-menu-profile">
                    <Person fontSize="small" sx={{ mr: 1.5 }} />
                    <Typography variant="body2">Profile</Typography>
                </MenuItem>

                {/* Inbox */}
                <MenuItem onClick={handleInboxClick} data-testid="user-menu-inbox">
                    <InboxIcon fontSize="small" sx={{ mr: 1.5 }} />
                    <Typography variant="body2">Inbox</Typography>
                    {unreadCount > 0 && (
                        <Badge
                            badgeContent={unreadCount}
                            color="error"
                            max={99}
                            sx={{ ml: 3 }}
                        />
                    )}
                </MenuItem>

                {/* Transactions */}
                <MenuItem onClick={() => { navigate('/account/transactions'); onClose(); }} data-testid="user-menu-transactions">
                    <Receipt fontSize="small" sx={{ mr: 1.5 }} />
                    <Typography variant="body2">Transactions</Typography>
                </MenuItem>

                {/* — Settings section — */}
                <Typography variant="overline" sx={{ px: 2, pt: 1.5, pb: 0.25, display: 'block', color: 'text.disabled', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                    Settings
                </Typography>

                {/* Payment */}
                <MenuItem onClick={() => { navigate('/payment-methods'); onClose(); }} data-testid="user-menu-payment">
                    <CreditCard fontSize="small" sx={{ mr: 1.5 }} />
                    <Typography variant="body2">Payment</Typography>
                </MenuItem>

                {/* Notification Preferences — collapsible, like Theme */}
                <MenuItem onClick={handleToggleNotifs} data-testid="user-menu-notifications">
                    <NotificationsIcon fontSize="small" sx={{ mr: 1.5 }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        Notifications
                    </Typography>
                    {notifsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </MenuItem>

                {/* Notification preferences section — inline, keeps menu open */}
                <Collapse in={notifsOpen} timeout="auto" unmountOnExit>
                    <Box
                        sx={{ px: 2, py: 1 }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        data-testid="user-menu-notifications-section"
                    >
                        <NotificationPreferencesCompact />
                    </Box>
                </Collapse>

                {/* Theme toggle */}
                <MenuItem onClick={handleTogglePrefs} data-testid="user-menu-preferences">
                    <Tune fontSize="small" sx={{ mr: 1.5 }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        Theme
                    </Typography>
                    {prefsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </MenuItem>

                {/* Theme section — inline, keeps menu open */}
                <Collapse in={prefsOpen} timeout="auto" unmountOnExit>
                    <Box
                        sx={{ px: 2, py: 1 }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        data-testid="user-menu-preferences-section"
                    >
                        <ThemeToggle />
                    </Box>
                </Collapse>

                {/* Settings (advanced) — hidden for now */}
                {/* <MenuItem onClick={handleSettingsClick} data-testid="user-menu-settings">
                    <SettingsOutlined fontSize="small" sx={{ mr: 1.5 }} />
                    <Typography variant="body2">Advanced</Typography>
                </MenuItem> */}

                {/* Spacer before sign out */}
                <Box sx={{ mt: 1 }} />

                {/* Sign out */}
                <MenuItem onClick={() => { (document.activeElement as HTMLElement)?.blur(); setLogoutConfirmOpen(true); onClose(); }} data-testid="user-menu-logout" sx={{ color: 'error.main' }}>
                    <Logout fontSize="small" sx={{ mr: 1.5 }} />
                    Sign out
                </MenuItem>
            </Menu>

            <Dialog open={logoutConfirmOpen} onClose={() => setLogoutConfirmOpen(false)} maxWidth="xs">
                <DialogTitle>Sign out</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to sign out?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLogoutConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleLogout} color="error" variant="contained">Sign out</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
